/**
 * TableBubbleMenu — bară flotantă centrată deasupra tabelului (NU pe cursor)
 * cu butoane pătrate stil Docmost (iconuri Tabler inline, grupate).
 *
 * Ordine:
 *   [col+stânga] [col+dreapta] [-col]   | [rând+sus] [rând+jos] [-rând]
 *   | [toggle header rând] [toggle header coloană] | [șterge tabel]
 */
import { BubbleMenu, type Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';

interface Props {
  editor: Editor;
}

/** Rect-ul tabelului curent (DOM) pentru a poziționa popover-ul deasupra lui. */
function getTableRect(editor: Editor): DOMRect | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'table') {
      const pos = $from.before(d);
      const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
      if (dom && typeof dom.getBoundingClientRect === 'function') {
        return dom.getBoundingClientRect();
      }
      break;
    }
  }
  return null;
}

export default function TableBubbleMenu({ editor }: Props) {
  const { t } = useTranslation();
  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => editor.isActive('table')}
      tippyOptions={{
        placement: 'top',
        duration: 120,
        offset: [0, 8],
        getReferenceClientRect: () => {
          const rect = getTableRect(editor);
          if (rect) return rect;
          // Fallback: poziția selecției (comportament default tippy).
          const { from } = editor.state.selection;
          const start = editor.view.coordsAtPos(from);
          return new DOMRect(start.left, start.top, 0, 0);
        },
      }}
    >
      <div
        className="flex items-center gap-0.5 rounded-md px-1 py-1"
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <IconBtn
          onClick={() => editor.chain().focus().addColumnBefore().run()}
          title={t('notes_blocks.table.add_column_left')}
        >
          <ColumnInsertLeftIcon />
        </IconBtn>
        <IconBtn
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          title={t('notes_blocks.table.add_column_right')}
        >
          <ColumnInsertRightIcon />
        </IconBtn>
        <IconBtn
          onClick={() => editor.chain().focus().deleteColumn().run()}
          title={t('notes_blocks.table.delete_column')}
        >
          <ColumnRemoveIcon />
        </IconBtn>
        <Sep />
        <IconBtn
          onClick={() => editor.chain().focus().addRowBefore().run()}
          title={t('notes_blocks.table.add_row_above')}
        >
          <RowInsertTopIcon />
        </IconBtn>
        <IconBtn
          onClick={() => editor.chain().focus().addRowAfter().run()}
          title={t('notes_blocks.table.add_row_below')}
        >
          <RowInsertBottomIcon />
        </IconBtn>
        <IconBtn
          onClick={() => editor.chain().focus().deleteRow().run()}
          title={t('notes_blocks.table.delete_row')}
        >
          <RowRemoveIcon />
        </IconBtn>
        <Sep />
        <IconBtn
          onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          title={t('notes_blocks.table.toggle_header_row')}
        >
          <TableRowIcon />
        </IconBtn>
        <IconBtn
          onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
          title={t('notes_blocks.table.toggle_header_column')}
        >
          <TableColumnIcon />
        </IconBtn>
        <Sep />
        <IconBtn
          onClick={() => editor.chain().focus().deleteTable().run()}
          title={t('notes_blocks.table.delete_table')}
          danger
        >
          <TrashXIcon />
        </IconBtn>
      </div>
    </BubbleMenu>
  );
}

function IconBtn({
  onClick,
  title,
  children,
  danger,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="w-8 h-8 grid place-items-center rounded transition-colors"
      style={{
        color: danger ? 'var(--danger)' : 'var(--text-muted)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = danger ? 'var(--danger)' : 'var(--text)';
        e.currentTarget.style.background = danger
          ? 'var(--danger-bg)'
          : 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = danger ? 'var(--danger)' : 'var(--text-muted)';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return (
    <span
      aria-hidden
      className="w-px h-4 self-center mx-1"
      style={{ background: 'var(--border-strong)' }}
    />
  );
}

// ============================================================
// Tabler icons inline (MIT). Toate sunt 18×18, stroke currentColor.
// ============================================================

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function ColumnInsertLeftIcon() {
  return (
    <svg {...iconProps}>
      <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1z" />
      <path d="M5 12l4 0" />
      <path d="M7 10l0 4" />
    </svg>
  );
}

function ColumnInsertRightIcon() {
  return (
    <svg {...iconProps}>
      <path d="M6 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1z" />
      <path d="M15 12l4 0" />
      <path d="M17 10l0 4" />
    </svg>
  );
}

function ColumnRemoveIcon() {
  return (
    <svg {...iconProps}>
      <path d="M6 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1z" />
      <path d="M16 10l4 4" />
      <path d="M16 14l4 -4" />
    </svg>
  );
}

function RowInsertTopIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 18v-4a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1z" />
      <path d="M12 9v-4" />
      <path d="M10 7l4 0" />
    </svg>
  );
}

function RowInsertBottomIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 6v4a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1z" />
      <path d="M12 15l0 4" />
      <path d="M14 17l-4 0" />
    </svg>
  );
}

function RowRemoveIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 6v4a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1z" />
      <path d="M10 16l4 4" />
      <path d="M10 20l4 -4" />
    </svg>
  );
}

function TableRowIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
      <path d="M9 3l-6 6" />
      <path d="M14 3l-7 7" />
      <path d="M19 3l-7 7" />
      <path d="M21 6l-4 4" />
      <path d="M3 10h18" />
      <path d="M10 10v11" />
    </svg>
  );
}

function TableColumnIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
      <path d="M10 10h11" />
      <path d="M10 3v18" />
      <path d="M9 3l-6 6" />
      <path d="M10 7l-7 7" />
      <path d="M10 12l-7 7" />
      <path d="M10 17l-4 4" />
    </svg>
  );
}

function TrashXIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 7h16" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
      <path d="M10 12l4 4m0 -4l-4 4" />
    </svg>
  );
}
